import { useTranslation } from '@fluxora/i18n';
import { Alert, Button } from '@fluxora/ui';
import { STOREFRONT_PATHS } from '@rheora/enrollment-contract';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useOptionalFunnel } from '../state/FunnelProvider';

/**
 * Pages that announce the link did NOT work. The session can still be a resumed
 * one (the customer opened the link once, then clicked it again from the email),
 * so without this the shop said "welcome back" directly above "this link has
 * expired" — two contradictory messages at once (found by browser QA).
 */
const LINK_FAILURE_PATHS: ReadonlySet<string> = new Set([
  STOREFRONT_PATHS.resumeExpired,
  STOREFRONT_PATHS.resumeBusy,
  // E-CRM04 — the phone check: nothing is restored yet, so no "welcome back"
  // banner above the page that asks for the number.
  STOREFRONT_PATHS.resumeConfirm,
  // …nor above the Continue press, before anything has been redeemed.
  STOREFRONT_PATHS.resumeContinue,
]);

/**
 * E-CRM04 — "Welcome back — we saved your progress".
 *
 * Shown when the funnel session was rebuilt from an emailed "resume your
 * enrollment" link (`GET /bff/funnel` → `resumed: true`, carried into the
 * provider's seed by `FunnelHydrationGate`). It also tells the lead, up front,
 * that the identity details are asked again — they are never stored, which is
 * why a resume always lands on the credit step at the furthest.
 *
 * Dismissal is local to this component (it lives in the storefront layout, so it
 * stays dismissed across in-funnel navigation). `role="status"`: a greeting, not
 * a fault.
 */
export function ResumeWelcomeBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const funnel = useOptionalFunnel();
  const resumed = funnel?.resumed === true;
  // E-CRM04 — a `progress` link (email never verified) restored only the plan
  // and usage, so the copy asks for the address and promises no prefill.
  const copy = funnel?.resumeScope === 'progress' ? 'shop.resume.banner.progress' : 'shop.resume.banner';
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  if (!resumed || dismissed || LINK_FAILURE_PATHS.has(pathname)) return null;

  return (
    <div className="mx-auto w-full max-w-6xl px-4 pt-6" data-testid="resume-welcome-banner">
      <Alert variant="success" title={t(`${copy}.title`)} role="status">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>{t(`${copy}.body`)}</span>
          <Button variant="ghost" size="sm" onClick={() => setDismissed(true)}>
            {t('shop.resume.banner.dismiss')}
          </Button>
        </div>
      </Alert>
    </div>
  );
}
